import CommonModal from 'components/common/CommonModal';
import DefaultButton from 'components/common/DefaultButton';
import DefaultImageView from 'components/common/DefaultImageView';
import DefaultText from 'components/common/DefaultText';
import LabelSelectButton from 'components/common/LabelSelectButton';
import {allowStateOption} from 'data/option';
import {allowStateStatus} from 'data/status';
import {
  changeAllowStateBackOffice,
  changeAllowStateBackOfficeRequestApproval,
} from 'function/api';
import {IPermissionState} from 'interfaces/interface';
import React, {useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {userInfoState} from 'utils/Atoms';
import {minmax} from 'utils/minmax';
import {useFormatMsg} from 'utils/useFormatMsg';

/**
 * 상태 변경 모달 컴포넌트의 Props 타입
 * @property {any} selectItem - 선택된 항목
 * @property {() => void} closeModal - 모달 닫기 함수
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsLoading - 로딩 상태 설정 함수
 * @property {React.Dispatch<React.SetStateAction<{}>>} setErrorModal - 에러 모달 설정 함수
 */
type Props = {
  selectItem: any;
  closeModal: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
};

/**
 * 상태 변경 모달 컴포넌트
 * 항목의 상태를 변경할 수 있는 모달 컴포넌트입니다.
 * @param {Props} props - 상태 변경 모달 컴포넌트의 props
 * @returns {JSX.Element} 상태 변경 모달 컴포넌트
 */
const ChangeStateModal = ({
  selectItem,
  closeModal,
  setIsLoading,
  setErrorModal,
}: Props) => {
  const userInfo = useRecoilValue(userInfoState);

  const [state, setState] = useState<IPermissionState | ''>('');

  const [completeModal, setCompleteModal] = useState(false);

  const completeDetailText = state
    ? `"${selectItem.name}" 의 상태가 "${useFormatMsg(
        allowStateStatus[state].text,
      )}" 변경되었습니다`
    : '';

  useEffect(() => {
    return () => {
      setState('');
      setCompleteModal(false);
    };
  }, []);

  return (
    <>
      <CommonModal closeModal={closeModal}>
        <BusinessModalContainer>
          <BusinessModalHeader>
            <DefaultText
              text="state"
              textStyle={{
                fontSize: minmax(40),
              }}
            />
            <DefaultImageView
              source="images/common/close.png"
              onClick={closeModal}
              style={{
                width: minmax(30),
                height: minmax(30),
                margin: minmax(15),
                cursor: 'pointer',
              }}
            />
          </BusinessModalHeader>

          <DefaultText
            text="변경할 상태를 선택해주세요."
            textStyle={{
              fontSize: minmax(30),
            }}
          />

          <LabelSelectButton
            value={state}
            setValue={setState}
            options={allowStateOption}
            buttonsWrapStyle={{justifyContent: 'center'}}
            buttonStyle={{width: '30%'}}
          />

          <DefaultButton
            text="change"
            actived={1}
            buttonStyle={{
              width: '100%',
              backgroundColor: state ? colors.primary2 : colors.lighterGray,
            }}
            textStyle={{fontSize: minmax(25)}}
            onClick={async () => {
              if (state) {
                let data: any = {
                  allowState: state,
                };

                if (selectItem.type === 'contractors')
                  data.contractorUUID = selectItem.uuid;
                if (selectItem.type === 'tong-places')
                  data.tongPlaceUUID = selectItem.uuid;
                if (selectItem.type === 'requestApproval') {
                  if (selectItem.contractorUUID)
                    data.contractorUUID = selectItem.contractorUUID;
                  data.tongPlaceTransactionUUID = selectItem.uuid;
                }
                let result;

                if (selectItem.type === 'requestApproval') {
                  result = await changeAllowStateBackOfficeRequestApproval({
                    userInfo,
                    setIsLoading,
                    setErrorModal,
                    data,
                  });
                } else {
                  result = await changeAllowStateBackOffice({
                    userInfo,
                    setIsLoading,
                    setErrorModal,
                    data,
                  });
                }
                if (result) setCompleteModal(true);
              }
            }}
          />
        </BusinessModalContainer>
      </CommonModal>
      {completeModal && (
        <CommonModal
          title="상태 변경 완료!"
          detail={completeDetailText}
          firstButtonText="confirm"
          firstButtonClick={() => {
            closeModal();
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default ChangeStateModal;

const BusinessModalContainer = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: ${minmax(20)};
`;

const BusinessModalHeader = styled.div`
  width: 100%;
  margin-bottom: ${minmax(10)};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;
